import {
  ERichMenuAreaActionType,
  TLineRichMenuDetail,
  TRichMenuAreaAction,
  TRichMenuTemplate
} from "types"

const RICH_MENU_MEM_CLICK_PATH = '/mem/richmenu/click'
const RICH_MENU_SURVEY_TRIGGER_PATTERN =
  /^__listy_survey:(\d+):[A-Za-z0-9_-]+__$/

function parseSurveyTriggerText(text: string): number | null {
  const match = text.match(RICH_MENU_SURVEY_TRIGGER_PATTERN)
  if (!match) return null

  const surveyId = Number(match[1])
  return Number.isSafeInteger(surveyId) && surveyId > 0 ? surveyId : null
}

/** メール導線のタップ先スキーム。 */
export const MAILTO_SCHEME = 'mailto:'

/** メール導線の入力値（アクション種別「メール」のフォーム状態）。 */
export type TRichMenuMailAction = {
  address: string
  subject: string
  body: string
}

/** タップ先URIがメール導線かを判定する。 */
export function isMailtoUri(uri: string): boolean {
  return uri.trim().toLowerCase().startsWith(MAILTO_SCHEME)
}

/**
 * 改行をCRLFへ正規化する。
 * mailto: の本文の改行は %0D%0A でなければならない。
 * @see https://www.rfc-editor.org/rfc/rfc6068.html#section-5
 */
function toCrlf(value: string): string {
  return value.replace(/\r\n|\r|\n/g, '\r\n')
}

/** 表示・編集用にCRLFをLFへ戻す（textareaの値はLFで扱う）。 */
function toLf(value: string): string {
  return value.replace(/\r\n/g, '\n')
}

/**
 * メール導線の入力値から mailto: URI を組み立てる。
 * 件名・本文はpercent-encodeする（未入力ならクエリごと省く）。
 *
 * 件名・本文の値は **trimしない**。この関数の結果は入力欄の value として
 * parseMailtoUri で戻されるため、ここで正規化すると入力途中のスペースや改行が
 * 打った瞬間に消えて入力できなくなる（build→parse を情報の落ちない往復に保つ）。
 */
export function buildMailtoUri(mail: TRichMenuMailAction): string {
  const params: string[] = []

  if (mail.subject.trim()) {
    params.push(`subject=${encodeURIComponent(mail.subject)}`)
  }
  if (mail.body.trim()) {
    params.push(`body=${encodeURIComponent(toCrlf(mail.body))}`)
  }

  const query = params.length > 0 ? `?${params.join('&')}` : ''

  return `${MAILTO_SCHEME}${mail.address.trim()}${query}`
}

/**
 * mailto: URI をフォームの入力値へ戻す。
 * LINEから読み戻したリッチメニューを編集フォームへprefillするために使う。
 * @returns 分解結果。mailto: でなければnull
 */
export function parseMailtoUri(uri: string): TRichMenuMailAction | null {
  if (!isMailtoUri(uri)) return null

  const rest = uri.trim().slice(MAILTO_SCHEME.length)
  const queryIndex = rest.indexOf('?')
  const address = queryIndex === -1 ? rest : rest.slice(0, queryIndex)
  const params = new URLSearchParams(
    queryIndex === -1 ? '' : rest.slice(queryIndex + 1),
  )

  return {
    address,
    subject: params.get('subject') ?? '',
    body: toLf(params.get('body') ?? ''),
  }
}

function unwrapRichMenuMemClickUri(uri: string): string | null {
  const trimmed = uri.trim()
  if (!trimmed) return null

  const url = new URL(trimmed)
  const path = url.pathname.replace(/\/+$/, '') || '/'
  if (!path.endsWith(RICH_MENU_MEM_CLICK_PATH)) return null

  const to = url.searchParams.get('to')
  const inner = to?.trim()

  return inner ? inner : null
}

function boundsKey(bounds: { x: number; y: number; width: number; height: number }) {
  return `${bounds.x},${bounds.y},${bounds.width},${bounds.height}`
}

export function mapLineRichMenuToTemplate(params: {
  templates: TRichMenuTemplate[]
  richMenu: TLineRichMenuDetail
}): { template: TRichMenuTemplate; areas: TRichMenuAreaAction[] } | null {
  const { templates, richMenu } = params

  const areaByBounds = new Map(
    richMenu.areas.map((a) => [boundsKey(a.bounds), a] as const),
  )

  const matched = templates.find((t) => {
    if (
      t.size.width !== richMenu.size.width ||
      t.size.height !== richMenu.size.height
    ) {
      return false
    }

    if (t.bounds.length !== richMenu.areas.length) return false

    return t.bounds.every((b) => areaByBounds.has(boundsKey(b)))
  })

  if (!matched) return null

  const nextAreas: TRichMenuAreaAction[] = matched.bounds.map((b) => {
    const area = areaByBounds.get(boundsKey(b))
    const action = area?.action

    switch (action?.type) {
      case ERichMenuAreaActionType.Uri: {
        const rawUri = action.uri?.trim() ?? ''
        return {
          type: ERichMenuAreaActionType.Uri,
          label: action.label,
          uri: unwrapRichMenuMemClickUri(rawUri) ?? rawUri,
        }
      }
      case ERichMenuAreaActionType.Message: {
        const text = action.text?.trim() ?? ''
        return {
          type: text ? ERichMenuAreaActionType.Message : ERichMenuAreaActionType.None,
          label: action.label,
          text: action.text,
        }
      }
      case 'postback': {
        const surveyId = parseSurveyTriggerText(action.data?.trim() ?? '')
        return surveyId
          ? {
              type: ERichMenuAreaActionType.Survey,
              label: action.label,
              surveyId,
            }
          : { type: ERichMenuAreaActionType.None }
      }
      default:
        return { type: ERichMenuAreaActionType.None }
    }
  })

  return { template: matched, areas: nextAreas }
}
